import { createUsePostHog } from '#bubba/shared/composables/tools/usePostHog';
import type { SharedPostHogEventName } from '#bubba/shared/types/posthog';

/**
 * Website-specific PostHog event names extending the shared base.
 */
type WebsitePostHogEventName =
  // ── Direct Booking ────────────────────────────────────
  | 'direct_booking_page_viewed'
  | 'direct_booking_service_selected'
  | 'direct_booking_location_selected'
  | 'direct_booking_slot_selected'
  | 'direct_booking_staff_selected'
  | 'direct_booking_confirmed'
  | 'direct_booking_auth_chosen'
  | 'direct_booking_account_created'
  | 'direct_booking_calendar_added'
  // ── Link Page ─────────────────────────────────────────
  | 'link_page_viewed'
  | 'link_page_link_clicked'
  | 'link_page_booking_clicked'
  // ── Review Page ───────────────────────────────────────
  | 'review_page_viewed'
  | 'review_page_auth_prompted'
  | 'review_page_sentiment_positive'
  | 'review_page_sentiment_negative'
  | 'review_page_submitted'
  | 'review_page_issue_submitted'
  // ── Marketing ─────────────────────────────────────────
  | 'newsletter_subscribed'
  | 'cta_clicked'
  // ── Navigation / Search ───────────────────────────────
  | 'search_performed'
  | 'search_result_clicked'
  | 'search_filter_applied'
  | 'search_location_detected'
  | 'search_location_denied'
  | 'search_no_results'
  | 'search_map_toggled'
  | 'search_page_loaded'
  // ── Hero / Header ─────────────────────────────────────
  | 'hero_service_selected'
  | 'hero_city_selected'
  | 'hero_search_submitted'
  | 'header_search_clicked'
  | 'header_service_selected';

export type PostHogEventName = SharedPostHogEventName | WebsitePostHogEventName;

/**
 * Type-safe composable for PostHog client-side tracking.
 *
 * Usage:
 * ```ts
 * import { usePostHog } from '#composables/usePostHog';
 * const { captureEvent, capturePageView, identifyUser } = usePostHog();
 * captureEvent('booking_confirmed', { bookingId: '123' });
 * ```
 */
export const usePostHog = createUsePostHog<PostHogEventName>();
